import { Attribute } from '../../decorators/attribute.decorator';
import { element } from '../../global/template-literals';
import { Component } from '../../decorators/component.decorator';
import { UIElement } from '../ui-element';
import { Child } from '../../decorators/child.decorator';
import {
    createElement,
    removeAttributes,
    setAttributes,
} from '../../global/ui-helpers';
import { ComponentState } from '../ui-element.const';
import styles from './ui-header-icon-button.css';
import type { UIHeaderIconButtonLayouts } from './ui-header.type';

// Unread counts wider than this are truncated by the badge, for example "99+".
const MAX_BADGE_DIGITS = 2;

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIHeaderIconButton
 * @element ui-header-icon-button
 * @classdesc Represents a class for ui-header-icon-button element.
 * @property {string} ariaLabel {@attr aria-label} Accessible name for the button. Required for accessibility.
 * @property {string} [glyph] {@attr glyph} Icon glyph code. For correct icon sizing, use icon codes starting with 'redesign-'. For example, 'redesign-bell'.
 * @property {string} [initialsGlyph] {@attr initials-glyph} Two letters to be used for the initials glyph. If this has a value provided, an initials glyph will be displayed instead of the regular glyph.
 * @property {UIHeaderIconButtonLayout} [layout] {@attr layout} Button layout type. Tertiary is default.
 *  {@desc tertiary}
 *  {@desc tonal}
 * @property {boolean} [showDropdown] {@attr show-dropdown} Toggle show dropdown chevron on the button.
 * @property {number} [badgeValue] {@attr badge-value} Shows badge with specified value. Values greater than 99 are displayed as "99+".
 * @example
 * <ui-header-icon-button glyph="redesign-bell" aria-label="Notifications" layout="tertiary" show-dropdown="true" badge-value="7">
 * </ui-header-icon-button>
 */
@Component({
    tag: 'ui-header-icon-button',
    styles,
})
export class UIHeaderIconButton extends UIElement {
    static readonly Layout: UIHeaderIconButtonLayouts = {
        Tertiary: 'tertiary',
        Tonal: 'tonal',
    };

    @Attribute() glyph: string | undefined;
    @Attribute() initialsGlyph: string | undefined;
    @Attribute() layout: string = UIHeaderIconButton.Layout.Tertiary;
    @Attribute(Boolean) showDropdown: boolean = false;
    @Attribute(Number) badgeValue: number = 0;

    @Child(':scope > button') button!: HTMLButtonElement;

    static get observedAttributes(): Array<string> {
        return ['badge-value', 'aria-expanded', 'initials-glyph', 'aria-label'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'badge-value': {
                this.updateBadgeValue();
                break;
            }
            case 'aria-expanded':
                if (newValue !== null) {
                    this.button.setAttribute('aria-expanded', newValue);
                } else {
                    this.button.removeAttribute('aria-expanded');
                }
                break;
            case 'initials-glyph':
                this.updateIcon();
                break;
            case 'aria-label':
                if (newValue !== null) {
                    this.button.setAttribute('aria-label', newValue);
                    this.removeAttribute('aria-label');
                }
                break;
            default:
                break;
        }
    }

    /**
     * @private
     */
    private updateIcon(): void {
        const glyphIcon = this.button.querySelector<HTMLElement>(
            ':scope > ui-icon:not(.ui-header-icon-button__dropdown-icon)'
        );
        const symbolicIcon = this.button.querySelector<HTMLElement>(
            ':scope > ui-icon-symbolic'
        );

        if (this.initialsGlyph) {
            if (symbolicIcon) {
                symbolicIcon.setAttribute('value', this.initialsGlyph);
                return;
            }

            glyphIcon?.remove();

            const icon = createElement({
                tagName: 'ui-icon-symbolic',
                attributes: {
                    value: this.initialsGlyph,
                },
            });

            if (icon) {
                this.button.insertBefore(icon, this.button.firstChild);
            }
            return;
        }

        symbolicIcon?.remove();

        if (this.glyph) {
            if (glyphIcon) {
                glyphIcon.setAttribute('glyph', this.glyph);
                return;
            }

            const icon = createElement({
                tagName: 'ui-icon',
                attributes: {
                    glyph: this.glyph,
                },
            });

            if (icon) {
                this.button.insertBefore(icon, this.button.firstChild);
            }
            return;
        }

        glyphIcon?.remove();
    }

    /**
     * @private
     */
    private updateBadgeValue(): void {
        const badgeElement = this.button.querySelector(
            '.ui-header-icon-button__badge'
        );
        const value = Math.round(this.badgeValue);
        if (badgeElement) {
            if (value > 0) {
                badgeElement.setAttribute('count', String(value));
            } else {
                badgeElement.remove();
            }
        } else if (value > 0) {
            const badge = createElement({
                tagName: 'ui-badge',
                attributes: {
                    'layout': 'pin',
                    'color': 'warning',
                    'content-type': 'count',
                    'count': String(value),
                    'max-digits': String(MAX_BADGE_DIGITS),
                },
                classList: {
                    'ui-header-icon-button__badge': true,
                },
            });
            if (badge) {
                const dropdownIcon = this.button.querySelector(
                    '.ui-header-icon-button__dropdown-icon'
                );
                this.button.insertBefore(badge, dropdownIcon);
            }
        }
    }

    // Attributes to be excluded from the button element.
    private excludedButtonAttributes = new Set<string>([
        'glyph',
        'initials-glyph',
        'class',
        'id',
        'show-dropdown',
        'badge-value',
        'layout',
    ]);

    /**
     * Passes click when invoked on host to the nested button. Serves as an API sugar allowing programmatic clicks on the host element.
     * @param {MouseEvent} event
     * @private
     */
    private handleHostClick = (event: MouseEvent) => {
        if (this.button && event.target === this) {
            this.button.dispatchEvent(
                new MouseEvent('click', { ...event, bubbles: false })
            );
        }
    };

    /**
     * @private
     */
    private subscribeEvents() {
        this.addEventListener('click', this.handleHostClick);
    }

    /**
     * @private
     */
    private unsubscribeEvents() {
        this.removeEventListener('click', this.handleHostClick);
    }

    /**
     * @inheritDoc
     */
    render() {
        const genericButtonAttributes = Array.from(this.attributes)
            .filter(
                (attribute: Attr) =>
                    !this.excludedButtonAttributes.has(attribute.name)
            )
            .reduce<Record<string, string>>((collection, attribute: Attr) => {
                collection[attribute.name] = attribute.value;
                return collection;
            }, {});

        const button = element`
            <button type="button" class="ui-header-icon-button__button">
                ${
                    this.glyph &&
                    !this.initialsGlyph &&
                    element`<ui-icon glyph="${this.glyph}"></ui-icon>`
                }
                ${
                    this.initialsGlyph &&
                    element`<ui-icon-symbolic value="${this.initialsGlyph}"></ui-icon-symbolic>`
                }
                ${
                    this.badgeValue > 0 &&
                    element`<ui-badge layout="pin" color="warning" class="ui-header-icon-button__badge" content-type="count" count="${Math.round(this.badgeValue)}" max-digits="${MAX_BADGE_DIGITS}"></ui-badge>`
                }
                ${
                    this.showDropdown &&
                    element`<ui-icon glyph="redesign-chevron-down" class="ui-header-icon-button__dropdown-icon"></ui-icon>`
                }
            </button>
        `;

        // Set current host attributes (excluding stop-list) on the button
        setAttributes(button, genericButtonAttributes);
        // Remove current attributes from the host (excluding stop-list)
        removeAttributes(this, Object.keys(genericButtonAttributes));

        this.append(button);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
